import { currentEnv } from './env';

/**
 * The terms somebody agrees to before they see anything.
 *
 * NOT LEGAL ADVICE, AND NOT REVIEWED BY A LAWYER. This is a careful draft
 * written to be read and understood, drawing on Trinidad and Tobago law as it
 * applies to electronic agreements. Before it is put in front of anybody
 * outside this team it should be read by an attorney admitted in Trinidad and
 * Tobago. That sentence belongs here rather than in a conversation that will be
 * forgotten.
 *
 * WHY A VERSION NUMBER. An agreement somebody accepted is the one they accepted
 * — not whatever the file says today. Changing the wording means a new version,
 * and anybody who accepted an older one is asked again. Without that, "they
 * agreed" is a claim nobody can substantiate, which is the same as not having
 * an agreement at all.
 *
 * WHY THE ENVIRONMENT CLAUSE IS SEPARATE. The confidentiality and the
 * restrictions are true everywhere. "This is a test copy and not everything
 * works" is true of the test copy alone, and telling somebody on the real board
 * that they are in a test environment is a false statement in a document whose
 * entire worth is that it is accurate.
 */

/**
 * Raise this when the WORDING changes in a way that alters what somebody has
 * agreed to. Everyone is then asked again, and what each person accepted stays
 * recorded against the version they saw.
 */
export const EULA_VERSION = 1;

export interface Clause {
  heading: string;
  /** Each entry is a paragraph. */
  body: string[];
}

/** True everywhere: the real board and the test copy alike. */
const COMMON: Clause[] = [
  {
    heading: 'Everything here is confidential',
    body: [
      'This board and everything in it — the work items, the notes, the comments, the names of people and organisations, the plans and the timings — is confidential information belonging to the board owner.',
      'You agree to keep it confidential, to use it only for the purpose you were given access for, and to protect it with at least the care you would use for your own confidential information.',
      'This obligation continues after your access ends. It does not apply to anything you can show was already public, was already properly known to you, or which you are required to disclose by law or by an order of a court — and if you are required to disclose it, you agree to tell the board owner first, unless the law forbids you from doing so.',
    ],
  },
  {
    heading: 'You may not copy anything out',
    body: [
      'You may not copy, download, export, screenshot, photograph, transcribe, print or otherwise reproduce any part of this board, except where the board itself provides a feature for doing so and you use it for the purpose you were given access for.',
      'You may not use anything here to train, fine-tune or prompt any machine learning or artificial intelligence system.',
      'This board and its contents are protected by copyright. Nothing here transfers any ownership or licence to you beyond permission to view it while your access lasts.',
    ],
  },
  {
    heading: 'Your access is yours alone',
    body: [
      'Your sign-in link and your access are personal to you. You may not share them, forward them, or let anybody else use your account — including colleagues, and including anybody at your own organisation.',
      'You may not show the board to anyone who has not been given their own access, whether in person, on a shared screen, or in a recording.',
      'If you believe somebody else has used your access, you agree to say so promptly.',
    ],
  },
  {
    heading: 'What you may not do',
    body: [
      'You may not attempt to reach any part of the board you have not been given access to, work around any restriction, or examine the system for weaknesses.',
      'You may not remove, alter or damage anybody else\'s work.',
      'Access may be withdrawn at any time, for any reason, without notice. When it ends you must stop using the board and delete anything you hold from it.',
    ],
  },
  {
    heading: 'No warranty, and limited liability',
    body: [
      'This board is provided as it is, with no warranty of any kind. The board owner does not promise it will be available, correct, or free of faults.',
      'To the fullest extent the law allows, the board owner is not liable for any loss arising from your use of this board — including lost data, lost profit, or any indirect or consequential loss. Nothing here limits liability for death or personal injury caused by negligence, or for fraud.',
    ],
  },
  {
    heading: 'The law that applies',
    body: [
      'This agreement is governed by the laws of the Republic of Trinidad and Tobago, and you submit to the exclusive jurisdiction of its courts.',
      'You accept this agreement electronically. Under the Electronic Transactions Act of Trinidad and Tobago, doing so has the same effect as signing it on paper.',
      'If any part of this agreement is found to be unenforceable, the rest of it continues to apply.',
      'This agreement may be updated. If it is, you will be asked to accept the new version before you can carry on using the board.',
    ],
  },
];

/** True of the test copy alone, and stated there alone. */
const TEST_ONLY: Clause = {
  heading: 'This is a test copy',
  body: [
    'This is a test environment. It exists so that the software can be tried out, and not everything works. Features may be missing, half-built, or wrong.',
    'Nothing here is real work, and nothing you do here is kept. The data may be changed, reset or deleted at any time without warning. Do not rely on anything you see, and do not put anything into it that matters.',
  ],
};

/**
 * The agreement, as it applies where it is being shown.
 *
 * The test paragraph comes FIRST where it applies: somebody needs to know they
 * are in a test copy before they read four screens of obligations about it.
 */
export function eulaClauses(env: string = currentEnv()): Clause[] {
  return env === 'live' ? COMMON : [TEST_ONLY, ...COMMON];
}

/** One line under the title, saying what this is before anybody reads it. */
export function eulaIntro(env: string = currentEnv()): string {
  return env === 'live'
    ? 'Before you can open this board, please read and accept the following.'
    : 'Before you can open this test copy, please read and accept the following.';
}

/**
 * WHETHER THE TERMS HAVE ACTUALLY BEEN PUT IN FRONT OF SOMEBODY.
 *
 * Agreeing to something you have not seen the end of is the weakest kind of
 * agreement there is, and the easiest to argue away afterwards. So the tick is
 * not offered until the last clause has been on the screen.
 *
 * TWO CASES THAT WOULD OTHERWISE TRAP PEOPLE, and neither is hypothetical:
 *
 *   · On a tall screen the terms may not overflow at all. There is no bottom to
 *     scroll to, so waiting for one means nobody on a large monitor can ever
 *     agree. Content that fits IS content that has been shown.
 *   · Browsers report these three numbers fractionally, and zoom makes it
 *     worse — 1199.6 of 1200 is the bottom. A strict comparison leaves people
 *     scrolling at a bar that will not move.
 *
 * @param scrollTop    how far down the box is scrolled
 * @param clientHeight how tall the visible part is
 * @param scrollHeight how tall the whole thing is
 */
export function readToEnd(scrollTop: number, clientHeight: number, scrollHeight: number): boolean {
  // Nothing to scroll: it is all on screen already.
  if (scrollHeight <= clientHeight + 1) return true;
  return scrollTop + clientHeight >= scrollHeight - 2;
}

/**
 * Whether this person still has to agree.
 *
 * An older version accepted is not this version accepted — that is the whole
 * point of the number.
 */
export function needsEula(accepted: number | undefined): boolean {
  return (accepted ?? 0) < EULA_VERSION;
}

/**
 * A THROWAWAY TEST IDENTITY, which is never gated by the terms.
 *
 * The confidentiality agreement protects the board owner's data from the people
 * it is shown to. The sample sign-ins we create to drive the test copy — the
 * roles-walk accounts, the vendor account, and the identities a witness signs in
 * as — are ours, not people the agreement is there to bind: their password is
 * written down in the open and they hold nothing real. They all live on the
 * `.test` top-level domain, which RFC 2606 reserves for exactly this and which a
 * real, deliverable email address can never use — so this cannot match a genuine
 * external viewer, and it is moot on the live board, where no `.test` account
 * exists. Without it, a per-account acceptance that a data reset wipes (or that a
 * duplicate per-tenant profile row never carried) stops an automated walk dead at
 * a gate the walker is correctly not allowed to accept (Vinnie, 2 Nov 2026 —
 * "fix the sticky EULA so witnesses aren't blocked").
 */
export function isTestIdentity(email: string | undefined): boolean {
  return !!email && email.toLowerCase().trim().endsWith('.test');
}

/**
 * WHETHER TO PUT THE TERMS IN FRONT OF SOMEBODY — the whole gate, as one
 * answerable question.
 *
 * `profilesOk` is the fact that makes this safe, and it is deliberately NOT
 * "an answer arrived" (`profilesKnown`). A FAILED read of the board's profiles
 * is an answer that arrived, and it is empty — and an empty profile reads
 * exactly like "has never accepted". That is how an established person, who
 * agreed to these terms long ago, was ejected to this screen whenever their
 * profiles subscription hit a transient error: the error wiped the map, the
 * map said nobody had agreed, and the gate believed it. So the terms are only
 * ever shown on a read that SUCCEEDED. A failed or not-yet-arrived read is not
 * "has not accepted"; it is "not known yet", and the board paints from the
 * copy the browser kept (which only exists because they were already past this
 * gate when they saved it) rather than throwing them back to the door.
 *
 * A genuinely new person is still asked: their read succeeds, carries no
 * accepted version, and `needsEula` is true.
 */
export function shouldShowEula(
  { staff, profilesOk, acceptedVersion, email }: { staff: boolean; profilesOk: boolean; acceptedVersion: number | undefined; email?: string | undefined }
): boolean {
  if (staff) return false;
  if (isTestIdentity(email)) return false;
  if (!profilesOk) return false;
  return needsEula(acceptedVersion);
}

/** What the record of an acceptance holds, so it can be produced later. */
export interface EulaAcceptance {
  eulaVersion: number;
  eulaAcceptedAt: number;
}

export const acceptanceRecord = (at: number): EulaAcceptance => ({
  eulaVersion: EULA_VERSION,
  eulaAcceptedAt: at,
});
